import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import BranchPickerModal from "@components/Doctor/[id]/BranchPickerModal";
import { getAvatarLink } from "@base/helpers/common";
import useToggle from "@base/hooks/useToggle";
import { apiLangForRecord } from "@base/modules/appointment/bookingCommon";
import { activeBranches, BookingDraft, BookingStep, withSite } from "@base/modules/appointment/regularBookingRules";
import { ISite } from "@base/modules/site/site.interface";
import siteService from "@base/modules/site/siteService";
import useGetState from "@base/modules/state/hooks/useGetState";

const PAGE_SIZE = 15;

interface IProps {
  draft: BookingDraft;
  onNext: (draft: BookingDraft, step?: BookingStep) => void;
}

/** Clinics taking regular bookings (`SERVICE_MENU`), then one of its active branches when it has any. */
export default function ChooseSiteStep({ draft, onNext }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const lang = apiLangForRecord(draft.record, i18n.resolvedLanguage);
  const { data: dataState } = useGetState();
  const { open: openBranches, shouldRender: shouldRenderBranches, toggle: toggleBranches } = useToggle();
  const [keyword, setKeyword] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [stateId, setStateId] = useState<string | number>();
  const [page, setPage] = useState<number>(0);
  const [sites, setSites] = useState<ISite[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pendingSite, setPendingSite] = useState<ISite>();

  //! function
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setIsLoading(true);
        const resp = await siteService.getListForBooking({
          page_number: page,
          page_size: PAGE_SIZE,
          lang,
          ...(searchText ? { search_text: searchText } : {}),
          ...(stateId !== undefined ? { state_id: stateId } : {}),
        });

        if (!ignore) {
          const list = resp.data.content_page ?? [];

          setSites((prev) => (page === 0 ? list : [...prev, ...list]));
          setTotalPage(resp.data.total_page ?? 0);
        }
      } catch (error) {
        if (!ignore && page === 0) {
          setSites([]);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [page, searchText, stateId, lang]);

  const search = useMemo(
    () =>
      debounce((value: string) => {
        setPage(0);
        setSearchText(value.trim());
      }, 600),
    [],
  );

  const choose = (site: ISite) => {
    if (activeBranches(site).length) {
      setPendingSite(site);
      toggleBranches();
      return;
    }

    onNext(withSite(draft, site, null));
  };

  //! render
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <CommonStyles.InputUI
            variant="filled"
            allowClear
            value={keyword}
            placeholder={t("booking.searchSite")}
            prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setKeyword(event.target.value);
              search(event.target.value);
            }}
          />
        </div>
        <CommonStyles.SelectUI
          variant="filled"
          allowClear
          placeholder={t("Area")}
          value={stateId}
          options={(dataState?.data ?? []).map((state) => ({ label: state.name, value: state.id }))}
          onChange={(value: string | number | undefined) => {
            setPage(0);
            setStateId(value ?? undefined);
          }}
        />
      </div>

      <div className="flex flex-col gap-3">
        {sites.map((site) => (
          <button
            type="button"
            key={site.code}
            onClick={() => choose(site)}
            className={`flex items-center gap-4 p-3 rounded-xl border text-left hover:border-common-primary ${
              site.code === draft.site?.code ? "border-common-primary bg-common-primary50" : "border-common-divider bg-common-white"
            }`}
          >
            <img
              className="w-14 h-14 rounded-lg object-contain shrink-0 bg-common-background"
              src={getAvatarLink(site.avatar ?? []) ?? CommonImages.SiteDefault}
              alt=""
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = CommonImages.SiteDefault;
              }}
            />
            <div className="grow min-w-0 flex flex-col gap-1">
              <span className="text-common-maintext text-base font-semibold font-['Inter'] line-clamp-2">{site.name}</span>
              <span className="text-common-subtext text-sm font-['Inter'] line-clamp-2">{site.address}</span>
            </div>
            <CommonIcons.RightOutlined className="text-common-subtext" />
          </button>
        ))}

        {isLoading ? <CommonStyles.LoadingUI /> : null}

        {!isLoading && !sites.length ? (
          <div className="w-full bg-common-background rounded-lg p-4 text-center text-common-subtext text-sm font-normal font-['Inter']">
            {t("noResult")}
          </div>
        ) : null}

        {!isLoading && page < totalPage - 1 ? (
          <div className="flex justify-center">
            <CommonStyles.ButtonUI ghost type="primary" onClick={() => setPage((prev) => prev + 1)}>
              {t("ShowMore")}
            </CommonStyles.ButtonUI>
          </div>
        ) : null}
      </div>

      {shouldRenderBranches && pendingSite ? (
        <BranchPickerModal
          open={openBranches}
          toggle={toggleBranches}
          branches={activeBranches(pendingSite)}
          onSelect={(branch) => {
            toggleBranches();
            onNext(withSite(draft, pendingSite, branch));
          }}
        />
      ) : null}
    </div>
  );
}
